import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { walletService, Wallet, WalletTransaction } from '../../services/api/walletService';

interface WalletState {
  wallets: Wallet[];
  selectedWalletId: string | null;
  /** 0 = wallet Principal activa, 1 = wallet Crédito activa (igual que wallet_selected en DB) */
  walletSelected: number;
  movements: WalletTransaction[];
  movementsPage: number;
  movementsLastPage: number;
  isLoading: boolean;
  isLoadingMovements: boolean;
  error: string | null;
  // Compatibilidad con el resto de la app (MainLayout usa wallet.balance)
  wallet: { balance: number; id: string } | null;
}

const initialState: WalletState = {
  wallets: [],
  selectedWalletId: null,
  walletSelected: 0,
  movements: [],
  movementsPage: 1,
  movementsLastPage: 1,
  isLoading: false,
  isLoadingMovements: false,
  error: null,
  wallet: null,
};

/** Carga todas las wallets del usuario */
export const fetchWallet = createAsyncThunk(
  'wallet/fetchWallet',
  async (_, { rejectWithValue }) => {
    try {
      const res = await walletService.getWallets();
      if (!res.success) throw new Error(res.message || 'Error al obtener wallets');
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Error al conectar con el servidor');
    }
  }
);

/** Carga los movimientos de una wallet específica */
export const fetchWalletMovements = createAsyncThunk(
  'wallet/fetchWalletMovements',
  async (
    { id, page = 1, filters }: {
      id: string;
      page?: number;
      filters?: { from_date?: string; to_date?: string; type?: number | string; process?: number | string };
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await walletService.getWalletMovements(id, page, filters);
      if (!res.success) throw new Error(res.message || 'Error al obtener movimientos');
      return { ...res.data, page };
    } catch (err: any) {
      return rejectWithValue(err.message || 'Error al conectar con el servidor');
    }
  }
);

// Alias para compatibilidad con código antiguo
export const fetchWalletTransactions = fetchWalletMovements;

const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
    selectWallet: (state, action) => {
      state.selectedWalletId = String(action.payload);
      state.movements = [];
      state.movementsPage = 1;
      // Actualizar wallet compat (MainLayout usa wallet.balance)
      const w = state.wallets.find(x => String(x.id) === String(action.payload));
      if (w) state.wallet = { id: String(w.id), balance: Number(w.amount) };
    },
  },
  extraReducers: (builder) => {
    // fetchWallet
    builder
      .addCase(fetchWallet.pending,    (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchWallet.fulfilled,  (state, action) => {
        state.isLoading = false;
        const { wallets, walletSelected } = action.payload || {};
        state.wallets = wallets || [];
        // Guardar walletSelected para que los componentes puedan determinar la wallet destino
        state.walletSelected = typeof walletSelected === 'number' ? walletSelected : 0;

        // Auto-selección basada en walletSelected:
        //   0 = preferir wallet Principal (is_primary=0)
        //   1 = preferir wallet Crédito  (is_primary=2)
        if (wallets && wallets.length > 0) {
          let preferred: any;
          if (walletSelected === 1) {
            // El usuario tiene crédito activo — seleccionar wallet de crédito
            preferred = wallets.find((w: any) => Number(w.is_primary) === 2);
          }
          // fallback: principal
          if (!preferred) preferred = wallets.find((w: any) => Number(w.is_primary) === 0) || wallets[0];
          state.selectedWalletId = String(preferred.id);
          state.wallet = { id: String(preferred.id), balance: Number(preferred.amount) };
        }
      })
      .addCase(fetchWallet.rejected,   (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // fetchWalletMovements
    builder
      .addCase(fetchWalletMovements.pending,    (state) => { state.isLoadingMovements = true; })
      .addCase(fetchWalletMovements.fulfilled,  (state, action) => {
        state.isLoadingMovements = false;
        const { data, current_page, last_page, page } = action.payload;
        state.movements = page === 1 ? (data || []) : [...state.movements, ...(data || [])];
        state.movementsPage = current_page;
        state.movementsLastPage = last_page;
      })
      .addCase(fetchWalletMovements.rejected,   (state, action) => {
        state.isLoadingMovements = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, selectWallet } = walletSlice.actions;
export default walletSlice.reducer;
